import React, { useState, useCallback } from 'react';
import {
  IonButton,
  IonItem,
  IonLabel,
  IonSpinner,
  IonIcon,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonAlert,
} from '@ionic/react';
import { add, trash, list } from 'ionicons/icons';
import { useParameters } from './parameters-provider';
import {
  IssueFlaggerTable,
  IssueTriageTable,
  StrategyRecommendationTable,
} from './parameters-schema';
import TagEditor from '../tags/tag-editor';
import NonProductTagEditor from '../tags/non-product-tag-editor';
import { IssuePriorityEditor } from './issue-priority-editor';
import EnhancedInferenceRuleEditor from './inference-rule-editor/enhanced-inference-rule-editor';
import EnhancedStrategyRecommendationEditor from './strategy-recommendation-editor/enhanced-strategy-recommendation-editor';
import EditorSelectionModal from './admin-editor-selection/editor-selection-modal';
import { EditorMode } from './admin-editor-selection/types';

/**
 * Admin Parameter Editor
 * 
 * This component allows administrators to create and edit:
 * - Products (tags marked as products)
 * - Inference Rules (Issue Flagger Tables)
 * - Issue Triage Order
 * - Strategy Recommendations
 */
const AdminParameterEditor: React.FC = () => {
  const { 
    parameters, 
    loadingParameters, 
    createParameter,
    updateParameter,
    deleteParameter,
  } = useParameters();
  
  const [mode, setMode] = useState<EditorMode>({ type: 'products' });
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);

  // Get filtered lists of parameters by type
  const inferenceRules = parameters?.filter(p => p.type === 'issueFlagger') ?? [];
  const issueTriage = parameters?.find(p => p.type === 'issueTriage');
  const strategyRecommendation = parameters?.find(p => p.type === 'strategyRecommendation');

  const handleCreateInferenceRule = async () => {
    try {
      const newRule: Omit<IssueFlaggerTable, 'id'> = {
        type: 'issueFlagger',
        displayTitle: 'New Inference Rule',
        parameterDescription: 'Description here',
        inputColumns: [],
        decisionTree: {
          type: 'issueFlaggerLeaf',
          output: {
            lightStatus: 'yellow',
            explanation: '',
            tags: []
          }
        }
      };
      
      const id = await createParameter(newRule);
      setMode({ type: 'inferenceRule', id });
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const handleCreateStrategyTable = async () => {
    try {
      const newStrategy: Omit<StrategyRecommendationTable, 'id'> = {
        type: 'strategyRecommendation',
        displayTitle: 'Strategy Recommendations',
        parameterDescription: 'Recommendations based on client data',
        inputColumns: [],
        decisionTree: {
          type: 'strategyRecommendationLeaf',
          output: {
            strategyMenu: [],
            explanation: '',
            extraTags: []
          }
        }
      };
      
      await createParameter(newStrategy);
      setMode({ type: 'strategyRecommendation' });
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const handleCreateTriageTable = async () => {
    try {
      const newTriage: Omit<IssueTriageTable, 'id'> = {
        type: 'issueTriage',
        issueFlaggerIds: inferenceRules.map(rule => rule.id)
      };
      
      await createParameter(newTriage);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const handleUpdateTriage = useCallback((newTriageTable: IssueTriageTable) => {
    if (issueTriage) {
      updateParameter(issueTriage.id, newTriageTable);
    }
  }, [issueTriage, updateParameter]);

  const handleDeleteRule = async () => {
    if (mode.type === 'inferenceRule' && mode.id) {
      try {
        await deleteParameter(mode.id);
        setMode({ type: 'products' }); // Return to products view after deletion
        setError(null);
      } catch (e) {
        setError((e as Error).message);
      }
    }
  };

  if (loadingParameters) return <IonSpinner />;

  const getCurrentModeLabel = () => {
    switch (mode.type) {
      case 'products':
        return 'Product List';
      case 'nonProductTags':
        return 'Tags';
      case 'issueTriage':
        return 'Issue Priority Order';
      case 'strategyRecommendation':
        return 'Strategy Recommendations';
      case 'inferenceRule': {
        const rule = inferenceRules.find(r => r.id === mode.id);
        return rule?.displayTitle || mode.id || 'Inference Rule';
      }
      default:
        return 'Select what to edit';
    }
  };

  return (
    <div className="admin-parameter-editor">
      {/* Navigation */}
      {/* NOTE: not using Tailwind yet, hard-coded max-width. */}
      <div style={{ maxWidth: '56rem', margin: '0 auto' }}>
        <div
          className="editor-menu-trigger"
          role="button"
          tabIndex={0}
          onClick={() => setIsSelectorOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setIsSelectorOpen(true);
            }
          }}
        >
          <span className="editor-menu-trigger__text">
            Currently editing: {getCurrentModeLabel()}
          </span>
          <IonButton
            className="editor-menu-trigger__button"
            fill="solid"
            size="small"
            onClick={() => setIsSelectorOpen(true)}
          >
            <IonIcon slot="icon-only" icon={list} />
          </IonButton>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <IonItem color="danger">
          <IonLabel>{error}</IonLabel>
        </IonItem>
      )}

      {/* Content area */}
      <div className="editor-content" style={{ marginTop: '1rem', width: '100%' }}>
        {mode.type === 'products' && (
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Product Management</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <p>Use this interface to manage the products that can be recommended to clients.</p>
              <TagEditor />
            </IonCardContent>
          </IonCard>
        )}

        {mode.type === 'nonProductTags' && (
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Tags Management</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <p>Use this interface to manage additional tags that can be used for extra categorization in strategy recommendations.</p>
              <NonProductTagEditor />
            </IonCardContent>
          </IonCard>
        )}

        {mode.type === 'inferenceRule' && mode.id && (
          <IonCard style={{ width: '100%', maxWidth: 'none' }}>
            <IonCardHeader>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <IonCardTitle>
                  Edit Inference Rule: {inferenceRules.find(r => r.id === mode.id)?.displayTitle || mode.id}
                </IonCardTitle>
                <IonButton
                  fill="clear"
                  color="danger"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <IonIcon slot="icon-only" icon={trash} />
                </IonButton>
              </div>
            </IonCardHeader>
            <IonCardContent>
              {inferenceRules.find(r => r.id === mode.id) && (
                <EnhancedInferenceRuleEditor
                  rule={inferenceRules.find(r => r.id === mode.id)! as IssueFlaggerTable}
                  onUpdate={newRule => updateParameter(mode.id!, newRule)}
                />
              )}
            </IonCardContent>
          </IonCard>
        )}

        {mode.type === 'issueTriage' && (
          <>
            {issueTriage && inferenceRules.length > 0 ? (
              <IssuePriorityEditor
                issueFlaggerTables={inferenceRules as IssueFlaggerTable[]} // Not actually necessary, but dev preview thows a compiler error that the IDE does not.
                triageTable={issueTriage as IssueTriageTable} // See above.
                onUpdate={handleUpdateTriage}
              />
            ) : (
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle>Issue Priority Order</IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  {inferenceRules.length > 0 ? (
                    <>
                      <p>No issue priority order has been defined yet.</p>
                      <IonButton
                        expand="block"
                        onClick={handleCreateTriageTable}
                      >
                        <IonIcon slot="start" icon={add} />
                        Initialize Issue Priority Order
                      </IonButton>
                    </>
                  ) : (
                    <p>No inference rules exist yet. Create some inference rules to establish their priority order.</p>
                  )}
                </IonCardContent>
              </IonCard>
            )}
          </>
        )}

        {mode.type === 'strategyRecommendation' && (
          <IonCard style={{ width: '100%', maxWidth: 'none' }}>
            <IonCardHeader>
              <IonCardTitle>Strategy Recommendations</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              {strategyRecommendation ? (
                <EnhancedStrategyRecommendationEditor
                  table={strategyRecommendation as StrategyRecommendationTable}
                  onUpdate={newTable => updateParameter(strategyRecommendation.id, newTable)}
                />
              ) : (
                <>
                  <p>No strategy recommendation table exists yet.</p>
                  <IonButton
                    expand="block"
                    onClick={handleCreateStrategyTable}
                  >
                    <IonIcon slot="start" icon={add} />
                    Create Strategy Table
                  </IonButton>
                </>
              )}
            </IonCardContent>
          </IonCard>
        )}
      </div>

      {/* Delete Confirmation Alert */}
      <IonAlert
        isOpen={showDeleteConfirm}
        onDidDismiss={() => setShowDeleteConfirm(false)}
        header="Delete Inference Rule"
        message="Are you sure you want to delete this inference rule? This action cannot be undone."
        buttons={[
          {
            text: 'Cancel',
            role: 'cancel',
          },
          {
            text: 'Delete',
            role: 'destructive',
            handler: handleDeleteRule,
          },
        ]}
      />

      <EditorSelectionModal
        isOpen={isSelectorOpen}
        onDismiss={() => setIsSelectorOpen(false)}
        inferenceRules={inferenceRules as IssueFlaggerTable[]}
        currentMode={mode}
        onSelectMode={setMode}
        onCreateInferenceRule={handleCreateInferenceRule}
      />

      <style>{`
        .editor-menu-trigger {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 0.5rem;
          margin: 0 auto;
          cursor: pointer;
          color: var(--ion-color-dark);
          border-bottom: 1px solid var(--ion-color-light);
          text-align: center;
        }

        .editor-menu-trigger__text {
          font-size: 1.1rem;
          font-weight: 600;
        }

        .editor-menu-trigger__button {
          pointer-events: none;
        }

        .editor-menu-trigger:focus {
          outline: 2px solid var(--ion-color-primary);
          outline-offset: 4px;
        }

        .strategy-editor {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .basic-info {
          background: var(--ion-color-light);
          border-radius: var(--ion-border-radius);
          overflow: hidden;
        }

        .basic-info ion-item {
          --background: transparent;
        }

        .editor-note {
          color: var(--ion-color-medium);
          text-align: center;
          padding: 1rem;
          background: var(--ion-color-light);
          border-radius: var(--ion-border-radius);
        }
      `}</style>
    </div>
  );
};

export default AdminParameterEditor;
